// Единый формат денег (CLAUDE.md, DESIGN.md §3) — один для интерфейса, сообщений платформы, Telegram и ответов
// агенту: `$1 234.50`, `−$23.00`, `+$20.73`, `1 250 000 сум`. Разряды — неразрывным пробелом, между знаком и «$» —
// невидимая склейка (U+2060): сумма никогда не рвётся переносом на «вчера −» / «$23.00».

/** Неразрывный пробел между разрядами. */
export const NBSP = ' ';
/** Склейка без ширины: запрещает перенос между знаком и «$» (оба — «префиксы», между ними браузер рвёт строку). */
export const WJ = '⁠';
const MINUS = '−';

/** Разряды по три цифры: «1234567» → «1 234 567». */
export function groupDigits(s: string): string {
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Модуль с округлением: «−0.001» при двух знаках — ноль, без минуса. */
function parts(n: number, digits: number): { neg: boolean; int: string; frac: string | undefined } {
  const [int = '0', frac] = Math.abs(n).toFixed(digits).split('.');
  return { neg: n < 0 && /[1-9]/.test(int + (frac ?? '')), int, frac };
}

/** `$1 234.50`, `−$23.00`. */
export function usd(n: number, digits = 2): string {
  const p = parts(n, digits);
  return `${p.neg ? MINUS + WJ : ''}$${groupDigits(p.int)}${p.frac ? `.${p.frac}` : ''}`;
}

/** Со знаком всегда: `+$20.73`, `−$11.40`, `$0.00`. */
export function signedUsd(n: number, digits = 2): string {
  const p = parts(n, digits);
  const zero = !/[1-9]/.test(p.int + (p.frac ?? ''));
  return `${zero ? '' : `${n > 0 ? '+' : MINUS}${WJ}`}${usd(Math.abs(n), digits)}`;
}

/** `1 250 000 сум`, `−15 000 сум` — целыми. */
export function sum(n: number): string {
  const p = parts(n, 0);
  return `${p.neg ? MINUS : ''}${groupDigits(p.int)}${NBSP}сум`;
}

/** Целое с разрядами: `61 200`. */
export function int(n: number): string {
  const p = parts(n, 0);
  return `${p.neg ? MINUS : ''}${groupDigits(p.int)}`;
}

/**
 * Сумма в валюте: сумы — `1 250 000 сум`, доллары (и неизвестная валюта) — `$1 234.50`, другая — `1 234.50 EUR`.
 * Для выплат сети и кабинетов Meta в своей валюте.
 */
export function money(n: number, currency?: string | null, digits = 2): string {
  const c = currency?.trim().toUpperCase();
  if (c === 'UZS') return sum(n);
  if (!c || c === 'USD') return usd(n, digits);
  const p = parts(n, digits);
  return `${p.neg ? MINUS : ''}${groupDigits(p.int)}${p.frac ? `.${p.frac}` : ''}${NBSP}${c}`;
}
